import type { ReactNode } from 'react';
import { ExternalLink, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent } from '~/components/ui/sheet';
import { Switch } from '~/components/ui/switch';
import { SwitchVisual } from '~/components/product/switch-visual';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Button } from '~/components/ui/button';
import { FlagGrid, type FlagItem } from '~/components/product/flag-grid';
import { cn } from '~/lib/utils';

export interface QuickEditProductSummary {
	name: string;
	sku?: string;
	barcode?: string;
	thumbUrl?: string;
}

export interface AvailabilityOption {
	value: string;
	label: string;
}

export interface QuickEditValue {
	storefrontVisible: boolean;
	availability: string;
	/** Single "Social/Shopping feeds" flag — collapses the separate Google Shopping + Facebook Catalog toggles. */
	showSocial: boolean;
	featured: boolean;
	used: boolean;
	/** Storefront state toggles. */
	stateNewRelease: boolean;
	stateOnSale: boolean;
	stateLimited: boolean;
	stateBestseller: boolean;
}

interface QuickEditSideSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	product: QuickEditProductSummary;
	value: QuickEditValue;
	onChange: (next: QuickEditValue) => void;
	availabilityOptions: AvailabilityOption[];
	/** Number of unsaved changes shown in the footer. */
	unsavedCount: number;
	onSave: () => void;
	onClose: () => void;
	/** Optional slot for the "open full product" link (route-aware wiring lives in the caller). */
	openFullProductSlot?: ReactNode;
}

function SectionLabel({ children }: { children: ReactNode }) {
	return <span className="text-[11px] font-medium uppercase tracking-[0.04em] text-foreground-subtle">{children}</span>;
}

/** Quick-edit side sheet for the Products grid. Presentational — `onSave`/`onClose` are callbacks. */
export function QuickEditSideSheet({
	open,
	onOpenChange,
	product,
	value,
	onChange,
	availabilityOptions,
	unsavedCount,
	onSave,
	onClose,
	openFullProductSlot
}: QuickEditSideSheetProps) {
	const { t } = useTranslation();
	const set = <K extends keyof QuickEditValue>(key: K, next: QuickEditValue[K]) => onChange({ ...value, [key]: next });

	const flagItems: FlagItem[] = [
		{ key: 'featured', label: t('products.quickEdit.featured'), checked: value.featured },
		{ key: 'used', label: t('products.quickEdit.used'), checked: value.used },
		{ key: 'stateNewRelease', label: t('products.quickEdit.newRelease'), checked: value.stateNewRelease },
		{ key: 'stateOnSale', label: t('products.quickEdit.onSale'), checked: value.stateOnSale },
		{ key: 'stateLimited', label: t('products.quickEdit.limited'), checked: value.stateLimited },
		{ key: 'stateBestseller', label: t('products.quickEdit.bestseller'), checked: value.stateBestseller }
	];

	const onFlagToggle = (key: string, checked: boolean) => set(key as keyof QuickEditValue, checked as never);

	return (
		<Sheet
			open={open}
			onOpenChange={next => {
				onOpenChange(next);
				if (!next) onClose();
			}}>
			<SheetContent side="right" showCloseButton className="w-full gap-0 p-0 sm:max-w-lg">
				{/* Header */}
				<div className="border-b border-border bg-background p-4">
					<div className="mb-3 flex items-center gap-1.5">
						<Zap size={12} className="text-foreground-subtle" />
						<SectionLabel>{t('products.quickEdit.title')}</SectionLabel>
					</div>
					<div className="mb-3 flex items-center gap-3">
						<span className="flex h-[60px] w-12 shrink-0 items-center justify-center overflow-hidden rounded-sm border border-border bg-surface">
							{product.thumbUrl ? <img src={product.thumbUrl} alt={product.name} className="h-full w-full object-contain" /> : null}
						</span>
						<div className="min-w-0 flex-1">
							<div className="text-sm font-semibold leading-tight text-foreground">{product.name}</div>
							<div className="mt-1 text-[11px] tabular-nums text-foreground-muted">
								{[product.sku && `SKU ${product.sku}`, product.barcode && `Barcode ${product.barcode}`].filter(Boolean).join(' · ')}
							</div>
						</div>
					</div>
					{openFullProductSlot ?? (
						<span className="inline-flex items-center gap-1 text-xs font-medium text-brand-subtle-fg">
							{t('products.quickEdit.openFull')}
							<ExternalLink size={11} />
						</span>
					)}
				</div>

				{/* Body */}
				<div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
					{/* Storefront visibility (prominent card) */}
					<div className="flex flex-col gap-2">
						<SectionLabel>{t('products.quickEdit.visibilitySection')}</SectionLabel>
						<div className="flex items-center justify-between gap-3 rounded-md border border-brand-border-subtle bg-brand-subtle px-4 py-3">
							<div className="min-w-0 flex-1">
								<div className="text-sm font-semibold text-foreground">{t('products.quickEdit.visibleTitle')}</div>
								<div className="mt-0.5 text-[11px] leading-tight text-foreground-muted">{t('products.quickEdit.visibleHelp')}</div>
							</div>
							<Switch
								checked={value.storefrontVisible}
								onCheckedChange={next => set('storefrontVisible', next)}
								aria-label={t('products.quickEdit.visibleTitle')}
							/>
						</div>
					</div>

					{/* Availability */}
					<div className="flex flex-col gap-2">
						<SectionLabel>{t('products.quickEdit.availabilitySection')}</SectionLabel>
						<Select value={value.availability} onValueChange={next => set('availability', next)}>
							<SelectTrigger className="w-full" aria-label={t('products.quickEdit.availabilitySection')}>
								<SelectValue placeholder={t('products.quickEdit.availabilityPlaceholder')} />
							</SelectTrigger>
							<SelectContent>
								{availabilityOptions.map(opt => (
									<SelectItem key={opt.value} value={opt.value}>
										{opt.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<span className="text-[11px] leading-snug text-foreground-muted">{t('products.quickEdit.availabilityHelp')}</span>
					</div>

					{/* Social / Shopping feeds — single collapsed toggle */}
					<div className="flex flex-col gap-2">
						<SectionLabel>{t('products.quickEdit.feedsSection')}</SectionLabel>
						<div
							role="switch"
							aria-checked={value.showSocial}
							tabIndex={0}
							onClick={() => set('showSocial', !value.showSocial)}
							onKeyDown={e => {
								if (e.key === ' ' || e.key === 'Enter') {
									e.preventDefault();
									set('showSocial', !value.showSocial);
								}
							}}
							className={cn(
								'flex cursor-pointer items-center justify-between gap-2 rounded-md border px-4 py-2.5 transition-colors',
								value.showSocial ? 'border-brand-border-subtle bg-brand-subtle' : 'border-border-subtle bg-background'
							)}>
							<span className="text-sm font-medium text-foreground">{t('products.quickEdit.socialFeeds')}</span>
							<SwitchVisual checked={value.showSocial} />
						</div>
					</div>

					{/* Product flags + states */}
					<div className="flex flex-col gap-2">
						<SectionLabel>{t('products.quickEdit.flagsSection')}</SectionLabel>
						<FlagGrid flags={flagItems} onToggle={onFlagToggle} columns={2} />
					</div>
				</div>

				{/* Footer */}
				<div className="flex items-center justify-between gap-2 border-t border-border bg-background p-4">
					<span className="text-[11px] font-medium text-foreground-muted">{t('products.quickEdit.unsaved', { count: unsavedCount })}</span>
					<div className="flex items-center gap-2">
						<Button type="button" variant="ghost" size="sm" onClick={onClose}>
							{t('common.cancel')}
						</Button>
						<Button type="button" size="sm" onClick={onSave} disabled={unsavedCount === 0}>
							{t('common.save')}
						</Button>
					</div>
				</div>
			</SheetContent>
		</Sheet>
	);
}
